// Runs list — the dashboard home. Pagination + search + filtering are
// server-side: the list endpoint takes limit/offset/query/status/mode and
// returns { items, total, limit, offset }. We fetch one page (25) at a time
// and re-fetch when the page / search / filter changes.

import { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import api from "../../api";
import useBssPoll from "../../hooks/useBssPoll";
import Icon from "./icons";
import NewRunModal from "./NewRunModal";
import ReleaseCutModal from "./ReleaseCutModal";
import AssignPrModal from "./AssignPrModal";
import PendingCommentTriggers from "./PendingCommentTriggers";
import PendingCheckTriggers from "./PendingCheckTriggers";
import Shell from "./Shell";
import {
  Avatar,
  EmptyState,
  ErrorState,
  LoadingState,
  RefreshButton,
  Spinner,
  StatusBadge,
  apiErrorMessage,
  costClass,
  formatDuration,
  formatMoney,
  isLive,
  relativeTime,
  rowStatusClass
} from "./shared";

const FILTERS = [
  ["all", "All"],
  ["running", "In flight"],
  ["failed", "Failed"],
  ["completed", "Completed"],
  ["queued", "Queued"],
  ["test_plan", "Test plans"]
];

const PAGE_SIZE = 25;

// Type-dropdown options — value is the server `mode` param ("" = no filter),
// label matches the Type-column badge so the filter and the column agree.
const TYPE_OPTIONS = [
  ["", "All types"],
  ["implement", "Implement"],
  ["review", "Review"],
  ["impact-analysis", "QA Impact"],
  ["browser-verify", "Browser QA"],
  ["release-test", "Release test"],
  ["test_plan", "Test plan"],
  ["address-comments", "Comments"],
  ["address-checks", "CI fix"],
  ["refresh", "Refresh"],
  ["adoption", "Adopted"],
  ["release-cut", "Release"],
  ["investigate", "Investigation"]
];

// Ticketless review runs are keyed by a PR ref (e.g. `boompay-client#2994`),
// which has no Jira ticket behind it. The `#` is the marker (real Jira keys
// never contain one) — hide the "Open ticket" action for those rows so it can't
// deep-link to a 404 /tickets page.
const isTicketKey = (k) => !!k && !k.includes("#");

// Filter tab -> server params. "running" is the "active" meta-status
// (anything not COMPLETED / FAILED / QUEUED); test_plan filters by mode.
function paramsForFilter(filter) {
  switch (filter) {
    case "running":
      return { status: "active" };
    case "failed":
      return { status: "FAILED" };
    case "completed":
      return { status: "COMPLETED" };
    case "queued":
      return { status: "QUEUED" };
    case "test_plan":
      return { mode: "test_plan" };
    default:
      return {};
  }
}

// Per-run-mode lead icon shown in the issue cell. Implement (the default)
// uses the boom-pink Bot; secondary modes get their own monochrome glyph so
// they're identifiable at a glance in a mixed list.
const MODE_ICON = {
  test_plan: { icon: "FileText", color: "var(--bss-info)" },
  refresh: { icon: "GitBranch", color: "var(--bss-info)" },
  "address-comments": { icon: "MessageSquare", color: "var(--bss-info)" },
  "address-checks": { icon: "AlertCircle", color: "var(--bss-danger)" },
  review: { icon: "Eye", color: "var(--bss-info)" },
  adoption: { icon: "Inbox", color: "var(--bss-fg-muted)" },
  "impact-analysis": { icon: "BarChart", color: "var(--bss-phase-reviewing)" },
  "browser-verify": { icon: "PlayCircle", color: "var(--bss-phase-reviewing)" },
  "release-test": { icon: "Rocket", color: "var(--bss-info)" },
  "release-cut": { icon: "GitMerge", color: "var(--bss-success)" },
  investigate: { icon: "Search", color: "var(--bss-boom-pink)" }
};
const DEFAULT_MODE_ICON = { icon: "Bot", color: "var(--bss-boom-pink)" };
function modeIconFor(mode) {
  return MODE_ICON[mode] || DEFAULT_MODE_ICON;
}

// Per-mode badge metadata for the dedicated Type column. Keeps the
// human-readable label + a color token so a glance at a row tells you
// "this is QA Impact" vs "this is a CI fix" without reading the issue
// cell. Implement is the default mode (no badge needed — that's the
// vast majority of rows; rendering "Implement" on every row is noise).
const MODE_BADGE = {
  test_plan: { label: "Test plan", color: "var(--bss-info)" },
  refresh: { label: "Refresh", color: "var(--bss-info)" },
  "address-comments": { label: "Comments", color: "var(--bss-info)" },
  "address-checks": { label: "CI fix", color: "var(--bss-danger)" },
  review: { label: "Review", color: "var(--bss-info)" },
  adoption: { label: "Adopted", color: "var(--bss-fg-muted)" },
  "impact-analysis": {
    label: "QA Impact",
    color: "var(--bss-phase-reviewing)"
  },
  "browser-verify": {
    label: "Browser QA",
    color: "var(--bss-phase-reviewing)"
  },
  "release-test": { label: "Release test", color: "var(--bss-info)" },
  "release-cut": { label: "Release", color: "var(--bss-success)" },
  investigate: { label: "Investigation", color: "var(--bss-boom-pink)" }
};
function ModeBadge({ mode }) {
  const meta = MODE_BADGE[mode];
  // Default-mode (implement) rows render `—` so the column stays visually
  // calm; non-implement rows show a colored pill that names the flow.
  if (!meta) {
    return <span style={{ color: "var(--bss-fg-subtle)" }}>—</span>;
  }
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "1px 8px",
        borderRadius: 999,
        border: `1px solid ${meta.color}`,
        color: meta.color,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.02em",
        whiteSpace: "nowrap"
      }}
    >
      {meta.label}
    </span>
  );
}

export default function RunsPage() {
  const history = useHistory();
  const [filter, setFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [page, setPage] = useState(0);
  const [modalMode, setModalMode] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [releaseCutOpen, setReleaseCutOpen] = useState(false);

  // Debounce the search box so each keystroke doesn't fire a request.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  // A new filter tab / dropdown / search term resets to the first page.
  useEffect(() => {
    setPage(0);
  }, [filter, typeFilter, projectFilter, debouncedQuery]);

  const fetchRuns = useCallback(
    () =>
      api
        .bssRuns({
          limit: PAGE_SIZE,
          offset: page * PAGE_SIZE,
          query: debouncedQuery || undefined,
          ...paramsForFilter(filter),
          // The Type dropdown wins over the (status-oriented) tab params —
          // only the legacy "Test plans" tab also sets `mode`.
          ...(typeFilter ? { mode: typeFilter } : {}),
          ...(projectFilter ? { project: projectFilter } : {})
        })
        .then((res) => res.data),
    [page, debouncedQuery, filter, typeFilter, projectFilter]
  );
  const { data, error, loading, stale, refresh } = useBssPoll(fetchRuns, {
    deps: [page, debouncedQuery, filter, typeFilter, projectFilter]
  });

  const runs = (data && data.items) || [];
  const total = (data && data.total) || 0;
  // Distinct Jira projects, server-computed over ALL runs (not the current
  // page/filter) so a selected project never hides the other options.
  const projects = (data && data.projects) || [];
  const offset = page * PAGE_SIZE;

  const openRun = (id) => history.push(`/bss/runs/${id}`);
  const openTicket = (key) =>
    history.push(`/bss/tickets/${encodeURIComponent(key)}`);

  // Toolbar + pending-trigger banners stay mounted across every refetch so
  // the search input doesn't flash on each keystroke (previously the entire
  // `body` was swapped to <LoadingState /> on loading=true, destroying the
  // input). Stale-while-revalidate: we only show LoadingState on the FIRST
  // load (no data yet); subsequent refetches keep the previous results
  // visible — refresh button already pulses to indicate the in-flight call.
  const toolbar = (
    <>
      <div className="bss-toolbar">
        <div className="bss-filter-tabs">
          {FILTERS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`bss-filter-tab ${
                filter === key ? "is-active" : ""
              }`.trim()}
              onClick={() => setFilter(key)}
            >
              {label}
            </button>
          ))}
        </div>
        <select
          className="bss-input"
          style={{ width: "auto", marginLeft: 8 }}
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          title="Filter by run type"
        >
          {TYPE_OPTIONS.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select
          className="bss-input"
          style={{ width: "auto", marginLeft: 8 }}
          value={projectFilter}
          onChange={(e) => setProjectFilter(e.target.value)}
          title="Filter by Jira project"
        >
          <option value="">All projects</option>
          {projects.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
        <div className="bss-search-wrap" style={{ flex: 1, marginLeft: 8 }}>
          <Icon.Search size={14} className="bss-icon" />
          <input
            className="bss-input"
            placeholder="Filter by issue key or summary…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </div>

      <PendingCommentTriggers onChange={refresh} />
      <PendingCheckTriggers onChange={refresh} />
    </>
  );

  let body;
  if (loading && !data) body = <LoadingState label="Loading runs…" />;
  else if (error && !data)
    // Initial load failed (no data yet) — blocking error state.
    body = <ErrorState message={apiErrorMessage(error)} onRetry={refresh} />;
  else
    body = (
      <>
        <div className="bss-card" style={{ overflow: "hidden" }}>
          <table className="bss-table">
            <thead>
              <tr>
                <th style={{ width: 120 }}>Issue</th>
                <th style={{ width: 110 }}>Type</th>
                <th>Summary</th>
                <th style={{ width: 140 }}>Status</th>
                <th style={{ width: 120 }}>Triggered by</th>
                <th style={{ width: 100, textAlign: "right" }}>Duration</th>
                <th style={{ width: 110, textAlign: "right" }}>Cost</th>
                <th style={{ width: 110 }}>Started</th>
                <th style={{ width: 90 }}>PR</th>
                <th style={{ width: 60 }} aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => {
                const live = isLive(r.status);
                const by = r.triggeredBy && r.triggeredBy.name;
                return (
                  <tr
                    key={r.id}
                    className={rowStatusClass(r.status, live)}
                    onClick={() => openRun(r.id)}
                  >
                    <td>
                      <div className="bss-table-cell-key">
                        {(() => {
                          const m = modeIconFor(r.mode);
                          const Ic = Icon[m.icon];
                          return <Ic size={14} style={{ color: m.color }} />;
                        })()}
                        <span>{r.key}</span>
                      </div>
                    </td>
                    <td>
                      {/* Dedicated Type column — replaces the inline-chip soup
                          that used to crowd the Issue cell. Implement (the
                          default) renders as `—` to keep the column quiet on
                          the common case; non-implement modes show a colored
                          pill (Review / QA Impact / CI fix / etc). */}
                      <ModeBadge mode={r.mode} />
                    </td>
                    <td>
                      <div className="bss-table-cell-summary">
                        <span>{r.summary || "—"}</span>
                        {r.status === "FAILED" && r.errorPreview && (
                          <span className="bss-preview-err">
                            <Icon.AlertTriangle size={11} />
                            {r.errorPreview}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <StatusBadge status={r.status} pulsing={live} />
                    </td>
                    <td>
                      {by ? (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8
                          }}
                        >
                          <Avatar name={by} size="sm" />
                          <span
                            style={{
                              fontSize: 12.5,
                              color: "var(--bss-fg-muted)"
                            }}
                          >
                            {by.split(" ")[0]}
                          </span>
                        </span>
                      ) : (
                        <span style={{ color: "var(--bss-fg-subtle)" }}>—</span>
                      )}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <span
                        className="bss-muted-mono"
                        style={{
                          color: live
                            ? "var(--bss-boom-pink)"
                            : "var(--bss-fg-muted)"
                        }}
                      >
                        {live && (
                          <Spinner
                            size={9}
                            style={{
                              marginRight: 4,
                              verticalAlign: -1,
                              color: "var(--bss-boom-pink)"
                            }}
                          />
                        )}
                        {formatDuration(r.durationSec)}
                      </span>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <span className={costClass(r.costUsd, r.budgetUsd)}>
                        {r.costUsd != null ? formatMoney(r.costUsd) : "—"}
                      </span>
                      <div
                        style={{
                          fontSize: 10.5,
                          color: "var(--bss-fg-subtle)",
                          marginTop: 2
                        }}
                      >
                        of {formatMoney(r.budgetUsd)}
                      </div>
                    </td>
                    <td>
                      <span
                        style={{
                          fontSize: 12.5,
                          color: "var(--bss-fg-muted)"
                        }}
                      >
                        {relativeTime(r.startedAt)}
                      </span>
                    </td>
                    <td>
                      {r.pr ? (
                        <a
                          href={r.pr.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bss-badge bss-badge--gray"
                          style={{
                            color: "var(--bss-info)",
                            textDecoration: "none"
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Icon.GitPullRequest size={11} />#{r.pr.number}
                        </a>
                      ) : (
                        <span
                          style={{
                            fontSize: 12,
                            color: "var(--bss-fg-subtle)"
                          }}
                        >
                          —
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="bss-row-actions">
                        {isTicketKey(r.key) && (
                          <button
                            type="button"
                            className="bss-icon-btn"
                            title="Open ticket"
                            onClick={(e) => {
                              e.stopPropagation();
                              openTicket(r.key);
                            }}
                          >
                            <Icon.ExternalLink size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {runs.length === 0 && (
            <EmptyState
              title="Nothing matches."
              hint="Try clearing the filter or your search."
            />
          )}
        </div>

        <div className="bss-list-foot">
          <div>
            {total === 0
              ? "showing 0 of 0"
              : `showing ${offset + 1}–${offset + runs.length} of ${total}`}
            {stale && (
              <span
                style={{ color: "var(--bss-warning, #b7791f)", marginLeft: 8 }}
                title={apiErrorMessage(error, "Last refresh failed")}
              >
                · reconnecting — showing last known data
              </span>
            )}
          </div>
          <div className="bss-pager">
            <button
              type="button"
              className="bss-btn bss-btn--ghost"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Prev
            </button>
            <button
              type="button"
              className="bss-btn bss-btn--ghost"
              disabled={offset + runs.length >= total}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </>
    );

  return (
    <Shell>
      <div className="bss-page-hd">
        <div>
          <h1>Runs</h1>
          <div className="bss-page-hd-sub">
            Engineers move a ticket to{" "}
            <span className="bss-code-inline">AUTO-SHIP</span> status — the
            agent picks it up here.
          </div>
        </div>
        <div className="bss-page-hd-right">
          <RefreshButton onClick={refresh} loading={loading} />
          <button
            type="button"
            className="bss-btn bss-btn--ghost"
            onClick={() => setAssignOpen(true)}
            title="File a PR under a ticket key (existing ticket or a new group)"
          >
            <Icon.Plus size={14} /> Assign PR…
          </button>
          <button
            type="button"
            className="bss-btn bss-btn--ghost"
            onClick={() => setReleaseCutOpen(true)}
            title="Build a release branch per repo from all PRs labeled 'release'"
          >
            <Icon.GitMerge size={14} /> Cut release…
          </button>
          <button
            type="button"
            className="bss-btn bss-btn--primary"
            onClick={() => setModalMode("implement")}
          >
            <Icon.Plus size={14} /> New run
          </button>
        </div>
      </div>

      {toolbar}
      {body}

      <AssignPrModal
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        onAssigned={refresh}
      />

      <ReleaseCutModal
        open={releaseCutOpen}
        onClose={() => setReleaseCutOpen(false)}
        onCut={(res) => {
          refresh();
          if (res?.rollupKey) {
            // ?pending=1 — the run rows are created a beat later by the queue;
            // the ticket page auto-retries on 404 instead of flashing empty.
            history.push(
              `/bss/tickets/${encodeURIComponent(res.rollupKey)}?pending=1`
            );
          }
        }}
      />

      <NewRunModal
        open={modalMode != null}
        initialMode={modalMode}
        onClose={() => setModalMode(null)}
        onLaunched={(res) => {
          refresh();
          if (!res) return;
          // implement fans out into a TicketGroup ({ groupId, status:"PLANNING" });
          // guard for a single-run shape ({ id, ... }) too so nothing breaks.
          if (res.groupId) history.push(`/bss/groups/${res.groupId}`);
          else if (res.id) history.push(`/bss/runs/${res.id}`);
        }}
      />
    </Shell>
  );
}
